import { BALANCE } from '../balance.ts';
import { Rng, type RngSnapshot } from '../rng.ts';
import type { PuzzleAct, PuzzleInstance, Role } from '../types.ts';
import {
  CAR_ROW,
  SIZE,
  carSolved,
  fits,
  legalSlides,
  slid,
  solveBlocks,
  type Block,
  type EscapeMove,
} from './escapeBoard.ts';
import { ESCAPE_LIBRARY } from './escapeLibrary.ts';
import {
  failResult,
  okResult,
  pickHolder,
  shown,
  type ActContext,
  type GenContext,
  type PuzzleModule,
  type SolveInfo,
  type TamperChoice,
  type TickContext,
  type TickOk,
  type ViewContext,
} from './types.ts';
import { alterableClues, clueByKind } from './util.ts';

export { SIZE, carSolved, legalSlides, solveBlocks } from './escapeBoard.ts';
export type { Block, EscapeMove, SolveResult } from './escapeBoard.ts';

/**
 * Escape (GDD §8.8): sliding blocks on a 6×6 grid, parking-lot style. The getaway car (block
 * 0, two cells long, on row 2) must reach the right edge. Police cars are extra blocks; with
 * the alarm high, reinforcements arrive, announced only on the driver's radio.
 *
 * Boards come from a pre-computed library (tools/gen-escape): hard sliding-block boards are
 * too slow to search for while a game starts. The seed picks the board; the level picks how
 * deep in the library's difficulty range; the alarm picks how many blocks.
 */

export interface EscapeParams {
  initial: Block[];
  /** Blocks painted as police cars. */
  policeCars: number;
  /** Fewest moves of the chosen board. */
  moves: number;
  /** Role that drives. */
  controller: Role;
}

export interface Announcement {
  atMs: number;
  /** Where the car comes from. */
  side: 'left' | 'right' | 'top' | 'bottom';
}

export interface EscapeState {
  blocks: Block[];
  moves: number;
  pending: { atMs: number; block: Block } | null;
  nextAtMs: number;
  reinforcements: number;
  rng: RngSnapshot;
}

/** Blocks besides the car at alarm 0, by level; police cars add to it. */
const BASE_OTHERS_BY_LEVEL = [3, 3, 4, 5, 5, 6] as const;
/** Where in a bucket's depth-sorted boards each level draws from (0 = easiest, 1 = hardest). */
const QUANTILE_BY_LEVEL = [0.1, 0.1, 0.3, 0.5, 0.75, 0.95] as const;
const MIN_OTHERS = 3;
const MAX_OTHERS = 12;
const LEAD_MS = 15_000;

const params = (p: PuzzleInstance) => p.params as EscapeParams;
const state = (p: PuzzleInstance) => p.state as EscapeState;

function generate(ctx: GenContext) {
  const { rng, level } = ctx;
  const police = Math.min(
    BALANCE.maxPoliceCars,
    Math.floor(ctx.alarmTenths / BALANCE.policeCarEveryTenths),
  );
  const others = Math.min(
    MAX_OTHERS,
    Math.max(MIN_OTHERS, (BASE_OTHERS_BY_LEVEL[level] ?? 4) + police),
  );
  const boards = ESCAPE_LIBRARY[others];
  if (!boards || boards.length === 0) throw new Error(`no escape boards for ${others} blocks`);
  const q = QUANTILE_BY_LEVEL[level] ?? 0.5;
  const index = Math.min(
    boards.length - 1,
    Math.max(0, Math.round(q * (boards.length - 1)) + rng.int(-1, 1)),
  );
  const [moves, defs] = boards[index] as (typeof boards)[number];

  const blocks: Block[] = defs.map(([x, y, len, h], id) => ({
    id,
    x,
    y,
    len,
    horiz: h === 1,
    police: false,
  }));
  const paint = rng
    .shuffle(blocks.filter((b) => b.id !== 0).map((b) => b.id))
    .slice(0, Math.min(police, others));
  for (const b of blocks) b.police = paint.includes(b.id);

  const controller = pickHolder(ctx.roles, ['driver', 'hacker']);
  const p: EscapeParams = {
    initial: blocks.map((b) => ({ ...b })),
    policeCars: police,
    moves,
    controller,
  };
  const s: EscapeState = {
    blocks,
    moves: 0,
    pending: null,
    nextAtMs: -1,
    reinforcements: 0,
    rng: rng.fork('reinforcements').snapshot(),
  };
  return {
    params: p,
    state: s,
    clues: [{ id: 'radio', kind: 'escapeRadio', holder: controller, value: null }],
    solvers: [controller],
  };
}

function apply(p: PuzzleInstance, act: PuzzleAct, ctx: ActContext) {
  if (act.kind !== 'escape.move') return failResult('invalid', 'not an escape action');
  const par = params(p);
  const st = state(p);
  if (ctx.actorRole !== par.controller) return failResult('notAllowed', 'you are not driving');
  if (!Number.isInteger(act.block) || !Number.isInteger(act.delta) || act.delta === 0) {
    return failResult('invalid', 'bad move');
  }
  if (!legalSlides(st.blocks, act.block).includes(act.delta)) {
    return failResult('illegalMove', 'the way is blocked');
  }
  st.blocks = st.blocks.map((b) => (b.id === act.block ? slid(b, act.delta) : b));
  st.moves += 1;
  return okResult({
    solved: carSolved(st.blocks),
    events: [
      { type: 'escape.move', data: { block: act.block, delta: act.delta, moves: st.moves } },
    ],
  });
}

function sideOf(b: Block): Announcement['side'] {
  if (b.horiz) return b.x === 0 ? 'left' : 'right';
  return b.y === 0 ? 'top' : 'bottom';
}

/** A police car that touches the border and fits, so that it can "drive in". */
function chooseReinforcement(rng: Rng, blocks: readonly Block[]): Block | null {
  const id = Math.max(...blocks.map((b) => b.id)) + 1;
  const candidates: Block[] = [];
  for (let i = 0; i < SIZE; i++) {
    candidates.push(
      { id, x: 0, y: i, len: 2, horiz: true, police: true },
      { id, x: SIZE - 2, y: i, len: 2, horiz: true, police: true },
      { id, x: i, y: 0, len: 2, horiz: false, police: true },
      { id, x: i, y: SIZE - 2, len: 2, horiz: false, police: true },
    );
  }
  return rng.shuffle(candidates).find((c) => fits(blocks, c)) ?? null;
}

/**
 * Adding a car must not make the puzzle unsolvable (GDD §16, item 13). Safe if the current
 * shortest solution never needs a cell the new car occupies; "inconclusive" counts as unsafe.
 */
export function safeToAdd(blocks: readonly Block[], block: Block): boolean {
  if (!fits(blocks, block)) return false;
  const r = solveBlocks(blocks, BALANCE.escapeSolveNodeCap);
  if (r.status !== 'solved') return false;
  let cur: readonly Block[] = blocks;
  for (const m of r.path) {
    cur = cur.map((b) => (b.id === m.block ? slid(b, m.delta) : b));
    if (!fits(cur, block)) return false;
  }
  return true;
}

function tick(p: PuzzleInstance, _dt: number, ctx: TickContext): TickOk {
  const st = state(p);
  const none: TickOk = { events: [], errorTenths: 0, sighted: false, solved: false };
  if (p.solved) return none;
  if (ctx.alarmTenths < BALANCE.policeAlertAt) return none;
  const rng = Rng.restore(st.rng);
  const events: TickOk['events'] = [];

  if (st.nextAtMs < 0) st.nextAtMs = ctx.execMs + BALANCE.reinforcementEveryMs;

  if (!st.pending && ctx.execMs >= st.nextAtMs - LEAD_MS) {
    const block = chooseReinforcement(rng, st.blocks);
    if (block) {
      st.pending = { atMs: st.nextAtMs, block };
      const clue = clueByKind(p, 'escapeRadio');
      delete clue.tampered;
      clue.value = { atMs: st.nextAtMs, side: sideOf(block) } satisfies Announcement;
      events.push({
        type: 'escape.radio',
        audience: ctx.players.filter((pl) => pl.role === clue.holder).map((pl) => pl.id),
        data: { atMs: st.nextAtMs, side: sideOf(block) },
      });
    } else {
      st.nextAtMs += BALANCE.reinforcementEveryMs;
    }
  }

  if (st.pending && ctx.execMs >= st.pending.atMs) {
    const block = st.pending.block;
    st.pending = null;
    const clue = clueByKind(p, 'escapeRadio');
    clue.value = null;
    delete clue.tampered;
    st.nextAtMs = ctx.execMs + BALANCE.reinforcementEveryMs;
    if (safeToAdd(st.blocks, block)) {
      st.blocks = [...st.blocks, block];
      st.reinforcements += 1;
      events.push({ type: 'escape.reinforcement', data: { block } });
    }
  }
  st.rng = rng.snapshot();
  return { events, errorTenths: 0, sighted: false, solved: false };
}

function view(p: PuzzleInstance, ctx: ViewContext): Record<string, unknown> {
  const par = params(p);
  const st = state(p);
  const clue = clueByKind(p, 'escapeRadio');
  const base: Record<string, unknown> = {
    type: 'escape',
    solved: p.solved,
    size: SIZE,
    carRow: CAR_ROW,
    controller: par.controller,
    // The whole team watches the grid (GDD §5.5); only the controller can move.
    blocks: st.blocks.map((b) => ({ ...b })),
    moves: st.moves,
    clues: {},
  };
  if (clue.holder === ctx.role && !ctx.tunnel) base['radio'] = shown(clue);
  if (ctx.revealed) base['secret'] = { pending: st.pending };
  return base;
}

function legalActs(p: PuzzleInstance, role: Role): PuzzleAct[] {
  const par = params(p);
  if (p.solved || role !== par.controller) return [];
  const st = state(p);
  const out: PuzzleAct[] = [];
  for (const b of st.blocks) {
    for (const delta of legalSlides(st.blocks, b.id)) {
      out.push({ kind: 'escape.move', block: b.id, delta });
    }
  }
  return out;
}

function tamper(p: PuzzleInstance, rng: Rng, exclude: Role | null): TamperChoice | null {
  if (alterableClues(p, exclude).length === 0) return null;
  const clue = clueByKind(p, 'escapeRadio');
  const v = clue.value as Announcement | null;
  if (!v) return null;
  const sides: Announcement['side'][] = ['left', 'right', 'top', 'bottom'];
  return {
    clueId: clue.id,
    value: {
      atMs: v.atMs + rng.pick([-8000, -5000, 5000, 8000]),
      side: rng.pick(sides.filter((s) => s !== v.side)),
    } satisfies Announcement,
  };
}

function solve(raw: unknown): SolveInfo {
  const par = raw as EscapeParams;
  const r = solveBlocks(par.initial, 200_000);
  return { solvable: r.status === 'solved', difficulty: r.status === 'solved' ? r.moves : -1 };
}

function autoSolve(p: PuzzleInstance): void {
  const st = state(p);
  st.blocks = st.blocks.map((b) => (b.id === 0 ? { ...b, x: SIZE - b.len } : b));
}

/** Convenience for bots: the moves that free the car on a visible board. */
export function solveEscape(blocks: readonly Block[], nodeCap: number): EscapeMove[] | null {
  const r = solveBlocks(blocks, nodeCap);
  return r.status === 'solved' ? r.path : null;
}

export const escapeModule: PuzzleModule = {
  type: 'escape',
  generate,
  apply,
  tick,
  view,
  legalActs,
  tamper,
  autoSolve,
  undo: () => undefined,
  solve,
};
